import React, {
  createContext,
  useContext,
  ReactNode,
  useSyncExternalStore,
} from "react";
import { jwtDecode } from "jwt-decode";
import {
  getToken,
  isSigned,
  setSession,
  clearSession,
  subscribe,
} from "../../services/authStorage";

export type AuthStatus = "authenticated" | "unauthenticated";

interface AuthContextProps {
  status: AuthStatus;
  isAuthenticated: boolean;
  /** Persist a freshly issued token and mark the session as signed in. */
  login: (token: string) => void;
  /** Clear the stored session. */
  logout: () => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

function isTokenValid(token: string | null): boolean {
  if (!token) return false;
  try {
    const { exp } = jwtDecode(token);
    return !!exp && exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

// Validate the stored JWT exactly once, synchronously, before any consumer
// renders — an expired/invalid token is dropped so guarded routes never flash
// protected content. The module-level flag keeps this a no-op on re-renders
// (and StrictMode's double invoke). Replaces the in-render check previously in
// App.tsx.
let didValidateSession = false;
function ensureSessionValidated(): void {
  if (didValidateSession) return;
  didValidateSession = true;
  if (isSigned() && !isTokenValid(getToken())) {
    clearSession();
  }
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  ensureSessionValidated();
  const isAuthenticated = useSyncExternalStore(subscribe, isSigned, isSigned);

  const value: AuthContextProps = {
    status: isAuthenticated ? "authenticated" : "unauthenticated",
    isAuthenticated,
    login: setSession,
    logout: clearSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
